'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Star } from 'lucide-react';

export function ProductCardHome({ product }) {
  const [imgError, setImgError] = useState(false);

  if (!product) return null;

  const {
    id,
    title,
    name,
    price,
    price_per_gm,
    dim_weight,
    oldPrice,
    images,
    href,
    currencySymbol = '₹',
    rating,
    product_variations,
    available_stock,
  } = product;

  const displayTitle = title || name || 'Untitled Product';
  const category = product.category;

  const getImageSrc = (img) =>
    typeof img === 'string' ? img : img.src;

  const mainImage =
    images && images.length > 0
      ? getImageSrc(images[0])
      : '/default/product.png';

  // ── Stock calculation (mirrors ProductCardPremium) ───────────────────────
  const getTotalStock = () => {
    if (product.product_total_stock !== undefined) return product.product_total_stock;
    if (available_stock !== undefined) return available_stock;
    if (!product_variations || product_variations.length === 0) return 0;

    const fromAggregate = product_variations.reduce((total, variation) => {
      const sum = variation.product_variant_sizes_aggregate?.aggregate?.sum?.quantity;
      return total + (sum ?? 0);
    }, 0);

    if (fromAggregate > 0) return fromAggregate;

    return product_variations.reduce((total, variation) => {
      return total + (variation.product_variant_sizes?.reduce(
        (sum, size) => sum + (size.quantity || 0), 0
      ) || 0);
    }, 0);
  };

  const totalStock = getTotalStock();
  const derivedIsClearance = totalStock >= 1 && totalStock <= 4;
  const isClearanceProduct = product.isClearanceProduct ?? derivedIsClearance;

  // ── Clearance price calculation (mirrors ProductCardPremium) ────────────
  const clearanceBasePrice = isClearanceProduct && price_per_gm && dim_weight
    ? parseFloat((price_per_gm * dim_weight).toFixed(2))
    : isClearanceProduct && price
    ? price
    : null;

  const effectivePrice = isClearanceProduct && clearanceBasePrice
    ? parseFloat((clearanceBasePrice * 0.9).toFixed(2))
    : price;

  const clearanceOriginalPrice = clearanceBasePrice;
  const effectiveOldPrice = isClearanceProduct ? undefined : oldPrice;
  const hasDiscount = effectiveOldPrice && price && effectiveOldPrice > price;
  const discountPercent = hasDiscount
    ? Math.round(((effectiveOldPrice - price) / effectiveOldPrice) * 100)
    : 0;
  const effectiveDiscountPercent = isClearanceProduct ? 10 : discountPercent;
  const showDiscountBadge = isClearanceProduct || hasDiscount;

  const productHref = isClearanceProduct
    ? `${href || `/product-detail/${id}`}?clearance`
    : (href || `/product-detail/${id}`);

  const formatPrice = (p) => {
    if (!p) return null;
    return p.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <>
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=Montserrat:wght@400;500;600&display=swap');

        .hpc-card {
          font-family: 'Montserrat', sans-serif;
          overflow: hidden;
          transition: box-shadow 0.35s ease, transform 0.35s ease;
          cursor: pointer;
        }

        .hpc-card:hover {
          box-shadow: 0 12px 40px rgba(191, 135, 93, 0.15);
          transform: translateY(-3px);
        }

        .hpc-img-wrap {
          position: relative;
          width: 100%;
          aspect-ratio: 4 / 3;
          overflow: hidden;
        }

        .hpc-img {
          object-fit: contain !important;
          object-position: center;
          transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1), filter 0.7s ease;
          filter: brightness(1) saturate(1.05);
        }

        .hpc-card:hover .hpc-img {
          transform: scale(1.07);
          filter: brightness(1.04) saturate(1.1);
        }

        .hpc-shimmer {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            120deg,
            transparent 30%,
            rgba(255,255,255,0.18) 50%,
            transparent 70%
          );
          transform: translateX(-100%);
          transition: transform 0.9s ease;
          z-index: 2;
          pointer-events: none;
        }
        .hpc-card:hover .hpc-shimmer { transform: translateX(100%); }

        .hpc-info {
          padding: 8px 10px 10px;
          border-top: 1px solid #f0ebe5;
          background: linear-gradient(to bottom, #fff, #fefdfb);
        }

        .hpc-category {
          font-size: 9px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #a8704e;
          font-weight: 500;
          margin-bottom: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .hpc-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 14px;
          font-weight: 600;
          color: #1a1a1a;
          line-height: 1.35;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          margin-bottom: 8px;
          transition: color 0.25s ease;
        }
        .hpc-card:hover .hpc-title { color: #bf875d; }

        @media (min-width: 640px)  { .hpc-title { font-size: 15px; } }
        @media (min-width: 1024px) { .hpc-title { font-size: 16px; } }

        .hpc-divider {
          height: 1px;
          background: linear-gradient(
            to right,
            transparent,
            #d4b896 40%,
            #d4b896 60%,
            transparent
          );
          margin-bottom: 8px;
        }

        .hpc-bottom {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 6px;
        }

        .hpc-price {
          font-size: 15px;
          font-weight: 700;
          color: #1a1a1a;
          letter-spacing: -0.02em;
          white-space: nowrap;
        }

        @media (min-width: 640px) { .hpc-price { font-size: 16px; } }

        .hpc-no-price {
          font-size: 11px;
          color: #64483c;
          font-weight: 500;
        }

        .hpc-stars {
          display: flex;
          align-items: center;
          gap: 2px;
          flex-shrink: 0;
        }

        .badge-premium {
          background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
        }

        .badge-clearance {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
        }
      `}</style>

      <div className="hpc-card rounded-lg">
        {/* Image */}
        <div className="hpc-img-wrap">
          <Link href={productHref} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
            <Image
              style={{ objectFit: "contain" }}
              src={imgError ? '/default/product.png' : mainImage}
              alt={displayTitle}
              fill
              className="hpc-img"
              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
              onError={() => setImgError(true)}
            />
            <div className="hpc-shimmer" />
          </Link>

          {/* Badges */}
 
        </div>

        {/* Info */}
        <div className="hpc-info">
          {category && <p className="hpc-category">{category}</p>}

          <Link href={productHref} target="_blank" rel="noopener noreferrer">
            <h3 className="hpc-title">{displayTitle}</h3>
          </Link>

          <div className="hpc-divider" />

          <div className="hpc-bottom">
            {effectivePrice ? (
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className="hpc-price">
                  {currencySymbol}{formatPrice(effectivePrice)}
                </span>
                {isClearanceProduct && clearanceOriginalPrice && (
                  <span className="text-[11px] text-[#64483c]/50 line-through font-medium">
                    {currencySymbol}{formatPrice(clearanceOriginalPrice)}
                  </span>
                )}
                {!isClearanceProduct && effectiveOldPrice && effectiveOldPrice !== effectivePrice && (
                  <span className="text-[11px] text-[#64483c]/50 line-through font-medium">
                    {currencySymbol}{formatPrice(effectiveOldPrice)}
                  </span>
                )}
              </div>
            ) : (
              <span className="hpc-no-price">Contact for Quote</span>
            )}

            {rating ? (
              <div className="hpc-stars">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-3 h-3 ${
                      i < Math.floor(rating)
                        ? 'fill-[#bf875d] text-[#bf875d]'
                        : 'text-[#e8e3dc]'
                    }`}
                  />
                ))}
              </div>
            ) : null}
          </div>

          {/* Inline "10% off" label for clearance */}
          {isClearanceProduct && (
            <div className="mt-1.5">
              <span className="text-[10px] font-semibold text-white bg-black px-1.5 py-0.5 rounded-sm">
                10% off
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ProductCardHome;